import SeoFooter from "../../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Plane, MapPin, Clock, Shield, Users, Car, ChevronRight, Phone, Mail, Globe, Star,
  CheckCircle2, Building2, GraduationCap, Briefcase, Heart, MapPinned, Wifi, Languages,
  ChevronDown, Quote
} from "lucide-react";
import PageTransition from "../../components/PageTransition";

const BrisbanePage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    { 
      icon: Plane, 
      title: "Airport Transfers", 
      desc: "Meet-and-greet service at Brisbane Airport (BNE), luggage assistance, and real-time flight monitoring for seamless arrivals and departures."
    },
    { 
      icon: Briefcase, 
      title: "Corporate Travel", 
      desc: "Privacy-forward transport with centralized invoicing, perfect for executive meetings and business conferences across Brisbane CBD."
    },
    { 
      icon: Heart, 
      title: "Weddings & Events", 
      desc: "Elegant conveyance with flawless timing for your special day. Red carpet service and decorated vehicles available."
    },
    { 
      icon: MapPinned, 
      title: "City Tours & Special Occasions", 
      desc: "Custom itineraries for high-profile guests exploring Brisbane's finest attractions, from South Bank to Mt Coot-tha."
    },
    { 
      icon: Shield, 
      title: "VIP Secure Transportation", 
      desc: "Discreet journeys for important clients with professional, security-conscious chauffeurs."
    },
    { 
      icon: Wifi, 
      title: "In-Vehicle Wi-Fi", 
      desc: "Stay connected en route with complimentary high-speed Wi-Fi in all our premium vehicles."
    }
  ];

  const whyChoose = [
    "Australian-based, luxury-trained chauffeurs with elite service standards",
    "Punctual, reliable schedules tailored to executive itineraries",
    "Luxurious fleet: Sedans, SUVs, Mercedes V-250, Sprinter Vans (up to 15 pax)",
    "Discreet, secure service for VIP and corporate travel",
    "In-vehicle Wi-Fi and multilingual chauffeurs available",
    "Transparent, premium pricing with clear quotes"
  ];

  const testimonials = [
    {
      quote: "Limo Near Me's Brisbane team provided flawless airport transfers for our executives. On-time, discreet, and impeccably professional.",
      author: "Head of Procurement",
      company: "Brisbane Tech Firm"
    },
    {
      quote: "Our corporate event logistics were seamless. Vehicles were pristine, chauffeurs multilingual, and the Wi-Fi kept our team connected.",
      author: "Events Director",
      company: "Brisbane Hospitality Group"
    },
    {
      quote: "We use Limo Near Me for regular client visits. Reliable, luxurious, and always on schedule.",
      author: "General Manager",
      company: "Brisbane Corporate Travel"
    }
  ];

  const faqs = [
    {
      q: "Do you offer airport meet-and-greet in Brisbane?",
      a: "Yes, we provide full meet-and-greet service at Brisbane Airport (BNE) for both domestic and international terminals. Your chauffeur will meet you at arrivals with a name board, assist with luggage, and escort you to your vehicle."
    },
    {
      q: "Are child seats available, and what ages do they cover?",
      a: "Yes, we provide child seats suitable for infants (0-6 months), toddlers (6 months - 4 years), and booster seats for children aged 4-7 years. Please request when booking."
    },
    {
      q: "How is gratuity handled for corporate rides?",
      a: "Gratuity is not included in our quoted prices and is entirely at the client's discretion. For corporate accounts, gratuity policies can be customized per your company guidelines."
    },
    {
      q: "What is your cancellation policy?",
      a: "Cancellations made 24+ hours before pickup receive a full refund. Cancellations within 24 hours may incur a 50% charge. No-shows are charged in full."
    },
    {
      q: "Do you support interstate transfers or cross-state travel?",
      a: "Yes, we offer interstate transfers including Brisbane to Gold Coast, Sunshine Coast, Byron Bay, and beyond. Long-distance pricing is available upon request."
    },
    {
      q: "Who pays for parking and tolls?",
      a: "All tolls are included in airport transfer quotes. For hourly bookings, tolls and parking are typically passed through at cost and itemized on your invoice."
    },
    {
      q: "Are vehicles wheelchair accessible?",
      a: "We can accommodate wheelchairs in our larger vehicles (Mercedes V-250 and Sprinter Vans). Please advise when booking so we can ensure the appropriate vehicle and assistance."
    }
  ];

  const popularRoutes = [
    { from: "Brisbane Airport (BNE)", to: "Brisbane CBD", price: "From $95" },
    { from: "Brisbane Airport (BNE)", to: "Gold Coast", price: "From $245" },
    { from: "Brisbane CBD", to: "Sunshine Coast", price: "From $255" },
    { from: "Brisbane Airport (BNE)", to: "Toowoomba", price: "From $370" },
    { from: "Brisbane CBD", to: "Noosa", price: "From $395" },
    { from: "Brisbane Airport (BNE)", to: "Byron Bay", price: "From $440" }
  ];

  const suburbs = [
    "Brisbane CBD", "South Brisbane", "Fortitude Valley", "New Farm", "Teneriffe",
    "Kangaroo Point", "West End", "Paddington", "Milton", "Toowong",
    "St Lucia", "Indooroopilly", "Chermside", "Carindale", "Mount Gravatt"
  ];

  // Schema.org structured data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Limo Near Me - Brisbane",
    "description": "Brisbane luxury chauffeur services for corporate travel and VIP transfers. On-time arrivals, premium fleet, in-vehicle Wi-Fi and multilingual chauffeurs.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Brisbane",
      "addressRegion": "QLD",
      "addressCountry": "AU"
    },
    "telephone": "+61481437772",
    "email": "info@limonearme.com.au",
    "url": "https://www.limonearme.com.au/chauffeur/brisbane",
    "priceRange": "$$-$$$",
    "openingHours": "Mo-Su 00:00-23:59",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127"
    },
    "areaServed": {
      "@type": "City",
      "name": "Brisbane"
    }
  };

  return (
    <PageTransition>
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Limo Near Me Brisbane | Luxury Chauffeur & Airport Transfer Services</title>
        <meta name="description" content="Brisbane luxury chauffeur services for corporate travel and VIP transfers. On-time arrivals, fleet of Luxury Sedans, SUVs, and Mercedes Sprinter Vans (up to 15 pax). In-vehicle Wi-Fi and multilingual chauffeurs available. Book now." />
        <meta name="keywords" content="Brisbane limo, Brisbane chauffeur, Brisbane airport transfer, corporate travel Brisbane, luxury car hire Brisbane, BNE airport limo" />
        <link rel="canonical" href="https://www.limonearme.com.au/chauffeur/brisbane" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/limo-logo.jpg" alt="Limo Near Me Brisbane" className="h-12 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-muted-foreground hover:text-foreground transition-colors">Services</Link>
              <Link to="/#fleet" className="text-muted-foreground hover:text-foreground transition-colors">Fleet</Link>
              <Link to="/#locations" className="text-muted-foreground hover:text-foreground transition-colors">Locations</Link>
              <Link to="/#contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+61481437772" className="hidden sm:flex items-center gap-2 text-primary">
                <Phone className="h-4 w-4" />
                <span className="font-semibold">+61 481 437 772</span>
              </a>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1566734904496-9309bb1798ae?w=1920&h=1080&fit=crop')" }}
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="absolute inset-0 hero-glow" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="initial" animate="animate" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <MapPin className="h-4 w-4" />
              <span className="text-sm font-medium">Brisbane, Queensland</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-foreground mb-6">
              Chauffeur Services<br />
              <span className="text-primary">in Brisbane</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Experience uncompromising luxury transport in Brisbane. Our premium fleet, multilingual chauffeurs, 
              and in-vehicle Wi-Fi deliver private, on-time transfers for airport arrivals, executive meetings, 
              events, and bespoke city experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold glow-primary">
                  Get Brisbane Quote <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-lg border-black/20 hover:bg-black/5">
                  <Phone className="mr-2 h-5 w-5" /> Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Why Choose Limo Near Me in Brisbane?
              </h2>
              <p className="text-muted-foreground mb-6">
                Brisbane's business and leisure travelers deserve the best. We deliver precision, privacy, 
                and luxury across airport transfers, executive travel, weddings, and special occasions.
              </p>
              <ul className="space-y-4 mb-8">
                {whyChoose.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/book">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Schedule an Executive Transfer <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&h=400&fit=crop" alt="Brisbane Airport Limo Transfer" className="rounded-2xl shadow-2xl" />
              <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-xl border border-black/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Plane className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">Brisbane Airport</p>
                    <p className="text-muted-foreground text-sm">BNE - All Terminals</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">What We Offer in Brisbane</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive luxury chauffeur services tailored for Brisbane's corporate and leisure travelers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card group hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">What Brisbane Clients Say</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Trusted by Brisbane's leading businesses and executives</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="dashboard-card"
              >
                <Quote className="h-8 w-8 text-primary/30 mb-4" />
                <p className="text-muted-foreground italic mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center gap-1 mb-2">
                  {[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4 text-yellow-500 fill-yellow-500" />)}
                </div>
                <p className="text-foreground font-semibold">{testimonial.author}</p>
                <p className="text-muted-foreground text-sm">{testimonial.company}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Brisbane Transfer Routes</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Fixed-price luxury transfers from Brisbane</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularRoutes.map((route, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="dashboard-card hover:border-primary/50 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-foreground font-medium">{route.from}</span>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span className="text-foreground font-medium">{route.to}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-black/10">
                  <Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Get Price</Button></Link>
                  <Link to="/book"><Button size="sm" variant="outline" className="rounded-full">Book <ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-card/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Brisbane FAQs</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Common questions about our Brisbane chauffeur services</p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="dashboard-card">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between text-left">
                  <h3 className="text-foreground font-semibold pr-4">{faq.q}</h3>
                  <ChevronDown className={`h-5 w-5 text-primary transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && <p className="text-muted-foreground text-sm mt-4 pt-4 border-t border-black/10">{faq.a}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Brisbane Service Areas</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">We cover all Brisbane suburbs and surrounding regions</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex flex-wrap justify-center gap-3">
            {suburbs.map((suburb, index) => (
              <span key={index} className="px-4 py-2 rounded-full bg-card border border-black/10 text-muted-foreground text-sm hover:border-primary/50 hover:text-primary transition-all cursor-default">{suburb}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Ready to Experience Brisbane Luxury?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">Schedule an executive transfer, open a corporate travel account, or speak with our travel concierge today.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-bold glow-primary">Get a Quote <ChevronRight className="ml-2 h-5 w-5" /></Button></Link>
              <a href="tel:+61481437772"><Button size="lg" variant="outline" className="rounded-full px-10 py-6 text-lg border-primary/50 hover:bg-black/5"><Phone className="mr-2 h-5 w-5" /> +61 481 437 772</Button></a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <SeoFooter />
    </div>
    </PageTransition>
  );
};

export default BrisbanePage;
